import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useHover } from 'javascripts/hooks/use-hover';
import { CdnImageUtils } from 'javascripts/shared/utils/cdn_image_utils';

import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Tooltip } from 'javascripts/components/tooltip';

import styles from 'stylesheets/components/social_sharing_button.module';

export function SocialSharingButton({ platform, label, onClickHandler, linkHref, linkTarget, withLabel }) {
  const [hoverRef, isHovered] = useHover();
  const [tooltipText, setTooltipText] = useState('');

  const imageSrc = useMemo(() => {
    return CdnImageUtils.getOptimizedImage(`sitewide/${platform}${isHovered ? '_hover' : ''}`);
  }, [platform, isHovered]);

  function renderButton(El, attrs) {
    return (
      <El className={styles.shareButton} onClick={onClickHandler} {...attrs}>
        <img className={styles.socialImageIcon} src={imageSrc} alt='' />
        {withLabel ? <p className={styles.buttonLabel}>{label}</p> : null}
      </El>
    );
  }

  function tooltipHelper(block) {
    if (!tooltipText) {
      return block;
    }

    return (
      <Tooltip text={tooltipText} placement='bottom' id='copy-social-sharing-link-tooltip' textSize='large'>
        {block}
      </Tooltip>
    );
  }

  function onCopyComplete() {
    setTooltipText('Copied to clipboard');
    onClickHandler();
  }

  function renderCopyButton() {
    return tooltipHelper(
      <CopyToClipboard text={linkHref} onCopy={onCopyComplete}>
        {renderButton('button', { 'aria-label': 'Copy Link to Clipboard' })}
      </CopyToClipboard>,
    );
  }

  function renderContent() {
    if (platform === 'link') return renderCopyButton();

    const el = platform === 'email' ? 'a' : 'button';
    const attrs = {
      onClick: onClickHandler,
      'aria-label': `Share to ${label}`,
    };

    if (platform === 'email') {
      attrs.href = linkHref;
      attrs.target = linkTarget;
    }

    return renderButton(el, attrs);
  }

  return (
    <div className={styles.shareButtonContainer} ref={hoverRef} data-testid='social-sharing-button'>
      {renderContent()}
    </div>
  );
}

SocialSharingButton.propTypes = {
  platform: PropTypes.oneOf(['facebook', 'pinterest', 'twitter', 'email', 'link']).isRequired,
  label: PropTypes.string,
  onClickHandler: PropTypes.func.isRequired,
  linkHref: PropTypes.string,
  linkTarget: PropTypes.string,
  withLabel: PropTypes.bool,
};
